"use client";

import Script from "next/script";
import { useCallback, useEffect, useId, useMemo } from "react";

import { tinaFieldSafe } from "@/utils";

type HubSpotForms = {
  meetings?: { create: (selector: string) => void };
  forms?: {
    create: (options: {
      portalId: string;
      formId: string;
      region?: string;
      target: string;
    }) => void;
  };
};

function getHbspt(): HubSpotForms | undefined {
  return (window as Window & { hbspt?: HubSpotForms }).hbspt;
}

function isHubSpotFormUrl(url: string): boolean {
  try {
    const host = new URL(url).hostname;
    return host === "share.hsforms.com" || host.endsWith(".hsforms.com");
  } catch {
    return url.includes("share.hsforms.com");
  }
}

/** Decode share.hsforms.com/1{formToken}{portalId36} into embed IDs. */
function decodeShareHsFormUrl(
  url: string,
): { portalId: string; formId: string } | null {
  try {
    const parsed = new URL(url);
    const token = parsed.pathname.split("/").filter(Boolean)[0] || "";
    if (!token) return null;

    const offset = token.startsWith("-") ? 2 : 1;
    const formB64 = token.substring(offset, offset + 22);
    const portalPart = token.substring(offset + 22);
    if (!formB64 || !portalPart) return null;

    const normalized = formB64.replaceAll("-", "+").replaceAll("_", "/");
    const bytes = Uint8Array.from(atob(normalized), (c) => c.charCodeAt(0));
    const formId = [...bytes]
      .map((byte, index) => {
        const hex = (byte + 256).toString(16).slice(-2);
        return ([4, 6, 8, 10].includes(index) ? "-" : "") + hex;
      })
      .join("");
    const portalId = parseInt(portalPart, 36);
    if (!formId || Number.isNaN(portalId)) return null;

    return { portalId: String(portalId), formId };
  } catch {
    return null;
  }
}

function toEmbedUrl(url: string): string {
  if (!url) return "";
  try {
    const parsed = new URL(url);
    parsed.searchParams.set("embed", "true");
    return parsed.toString();
  } catch {
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}embed=true`;
  }
}

function initMeetingsEmbed() {
  getHbspt()?.meetings?.create?.(".meetings-iframe-container");
}

function HubSpotFormEmbed({
  sourceUrl,
  formSection,
}: {
  sourceUrl: string;
  formSection?: { meetingEmbedUrl?: string | null } | null;
}) {
  const decoded = useMemo(() => decodeShareHsFormUrl(sourceUrl), [sourceUrl]);
  const reactId = useId().replace(/:/g, "");
  const targetId = `hs-form-${reactId}`;
  const createForm = useCallback(() => {
    if (!decoded) return;
    const forms = getHbspt()?.forms;
    const target = document.getElementById(targetId);
    if (!forms?.create || !target) return;
    target.innerHTML = "";
    forms.create({
      portalId: decoded.portalId,
      formId: decoded.formId,
      region: "na1",
      target: `#${targetId}`,
    });
  }, [decoded, targetId]);

  useEffect(() => {
    createForm();
  }, [createForm]);

  if (!decoded) {
    return (
      <iframe
        src={sourceUrl}
        title="HubSpot form"
        className="w-full min-h-[720px] border-0"
        data-tina-field={tinaFieldSafe(formSection, "meetingEmbedUrl")}
      />
    );
  }

  return (
    <>
      <div
        id={targetId}
        className="hs-inline-form bg-white w-full min-w-0 overflow-hidden px-4 py-5 md:px-5 md:py-6 [&_form]:m-0 [&_form]:max-w-full [&_.hs-form]:max-w-full [&_.hs-form-field]:max-w-full [&_.hs-input]:max-w-full [&_.hs-button]:max-w-full [&_ul]:pl-0"
        data-tina-field={tinaFieldSafe(formSection, "meetingEmbedUrl")}
      />
      <Script
        src="https://js.hsforms.net/forms/embed/v2.js"
        strategy="afterInteractive"
        onReady={createForm}
      />
    </>
  );
}

interface HubSpotMeetingsEmbedProps {
  url?: string | null;
  formSection?: { meetingEmbedUrl?: string | null } | null;
}

export default function HubSpotMeetingsEmbed({
  url,
  formSection,
}: HubSpotMeetingsEmbedProps) {
  const sourceUrl = url ?? formSection?.meetingEmbedUrl ?? "";

  if (!sourceUrl) {
    return null;
  }

  if (isHubSpotFormUrl(sourceUrl)) {
    return <HubSpotFormEmbed sourceUrl={sourceUrl} formSection={formSection} />;
  }

  const embedUrl = toEmbedUrl(sourceUrl);

  return (
    <>
      <div
        className="meetings-iframe-container w-full min-w-0 min-h-[1800px] md:min-h-[600px] [&>iframe]:!min-h-[1800px] md:[&>iframe]:!min-h-[600px] "
        data-src={embedUrl}
        data-tina-field={tinaFieldSafe(formSection, "meetingEmbedUrl")}
      />
      <Script
        src="https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js"
        strategy="afterInteractive"
        onReady={initMeetingsEmbed}
      />
    </>
  );
}
